import { CarEnum, ClientEnum, ENABLED_CAR_TYPES, EnabledCarTypes } from "./types"

// The car-type menu only makes sense when there is more than one type to pick.
export const hasCarTypeChoice = (
  enabled: EnabledCarTypes = ENABLED_CAR_TYPES
) => enabled.length > 1

// The car type the funnel uses for a URL: the chosen type when it is enabled,
// the only enabled type when the menu is skipped, and undefined while the
// visitor still has to choose.
export const resolveCarType = (
  carType?: CarEnum,
  enabled: EnabledCarTypes = ENABLED_CAR_TYPES
): CarEnum | undefined => {
  if (carType && enabled.includes(carType)) return carType
  return hasCarTypeChoice(enabled) ? undefined : enabled[0]
}

export type FunnelLocation = {
  clientType?: ClientEnum
  carType?: CarEnum
  product?: string
  step?: number | string
  lang?: string | null
}

export const buildPath = ({
  clientType,
  carType,
  product,
  step,
  lang
}: FunnelLocation) => {
  let path = "/"
  if (clientType) path += `${clientType}/`
  if (carType) path += `${carType}/`
  if (product) path += `${product}/`
  if (step) path += `${step}/`
  if (lang) path += `?lang=${lang}`
  return path
}

const isClientType = (value?: string): value is ClientEnum =>
  Object.values(ClientEnum).includes(value as ClientEnum)

const isCarType = (value?: string): value is CarEnum =>
  Object.values(CarEnum).includes(value as CarEnum)

// Where to send a funnel URL whose car type is missing or disabled: the product
// list of the car type the funnel uses, keeping the whole query string so
// campaign parameters (utm_*, gclid) survive for attribution. Anything else is
// left alone: invalid values are rejected by the page, and /error or /success
// also match the funnel's path pattern but are not funnel URLs.
export const getCarTypeRedirect = (
  { clientType, carType }: { clientType?: string; carType?: string },
  search = "",
  enabled: EnabledCarTypes = ENABLED_CAR_TYPES
): string | undefined => {
  if (!isClientType(clientType)) return undefined
  const chosenCarType = isCarType(carType) ? carType : undefined
  if (carType && !chosenCarType) return undefined

  const resolvedCarType = resolveCarType(chosenCarType, enabled)
  if (resolvedCarType === chosenCarType) return undefined

  return buildPath({ clientType, carType: resolvedCarType }) + search
}
